import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import {
  FaReact,
  FaHtml5,
  FaCss3Alt,
  FaGitAlt,
  FaGithub,
  FaPython,
  FaLaravel,
  FaRocket,
} from "react-icons/fa";
import {
  SiTailwindcss,
  SiMysql,
  SiJavascript,
  SiTypescript,
  SiPhp,
  SiBootstrap,
  SiGitlab,
  SiSupabase,
} from "react-icons/si";
import { VscVscode } from "react-icons/vsc";
import { Layers, Database, Server, Wrench, Sparkles } from "lucide-react";

const skillCategories = [
  { id: "all", labelKey: "skills.categories.all", icon: Sparkles },
  { id: "frontend", labelKey: "skills.categories.frontend", icon: Layers },
  { id: "backend", labelKey: "skills.categories.backend", icon: Server },
  { id: "database", labelKey: "skills.categories.database", icon: Database },
  { id: "tools", labelKey: "skills.categories.tools", icon: Wrench },
];

const skillGroups = [
  {
    id: "frontend",
    titleKey: "skills.categories.frontend",
    icon: Layers,
    skills: [
      {
        name: "React.js",
        icon: FaReact,
        color: "#61dafb",
        tag: "Core UI Library",
      },
      {
        name: "TypeScript",
        icon: SiTypescript,
        color: "#3178c6",
        tag: "Typed JavaScript",
      },
      {
        name: "JavaScript (ES6+)",
        icon: SiJavascript,
        color: "#f7df1e",
        tag: "Modern Web Logic",
      },
      {
        name: "Tailwind CSS",
        icon: SiTailwindcss,
        color: "#06b6d4",
        tag: "Utility-First Styling",
      },
      {
        name: "HTML5",
        icon: FaHtml5,
        color: "#e34f26",
        tag: "Semantic Structure",
      },
      {
        name: "CSS3",
        icon: FaCss3Alt,
        color: "#1572b6",
        tag: "Responsive Layouts",
      },
      {
        name: "Bootstrap",
        icon: SiBootstrap,
        color: "#7952b3",
        tag: "UI Components",
      },
    ],
  },
  {
    id: "backend",
    titleKey: "skills.categories.backend",
    icon: Server,
    skills: [
      {
        name: "Laravel",
        icon: FaLaravel,
        color: "#ff2d20",
        tag: "PHP Web Framework",
      },
      {
        name: "PHP",
        icon: SiPhp,
        color: "#777bb4",
        tag: "Backend Language",
      },
      {
        name: "Supabase",
        icon: SiSupabase,
        color: "#3ECF8E",
        tag: "BaaS & Authentication",
      },
    ],
  },
  {
    id: "database",
    titleKey: "skills.categories.database",
    icon: Database,
    skills: [
      {
        name: "MySQL",
        icon: SiMysql,
        color: "#4479a1",
        tag: "Relational Database",
      },
      {
        name: "Supabase",
        icon: SiSupabase,
        color: "#3ECF8E",
        tag: "PostgreSQL BaaS",
      },
    ],
  },
  {
    id: "tools",
    titleKey: "skills.categories.tools",
    icon: Wrench,
    skills: [
      {
        name: "Antigravity",
        icon: FaRocket,
        color: "#4285F4",
        tag: "AI Coding Assistant",
      },
      {
        name: "VS Code",
        icon: VscVscode,
        color: "#007acc",
        tag: "Primary Editor",
      },
      {
        name: "Git",
        icon: FaGitAlt,
        color: "#f05032",
        tag: "Version Control",
      },
      {
        name: "GitHub",
        icon: FaGithub,
        color: "#8b949e",
        tag: "Collaboration & CI",
      },
      {
        name: "GitLab",
        icon: SiGitlab,
        color: "#fc6d26",
        tag: "DevOps & Repositories",
      },
    ],
  },
];

// Additional standalone skills that belong to general skills (e.g. Python for ML/scripting)
const additionalSkills = [
  {
    name: "Python",
    icon: FaPython,
    color: "#3776ab",
    tag: "Scripting & Data Analysis",
    groupTitle: "Scripting & Data",
  },
];

export default function Skills() {
  const { t } = useTranslation();
  const [activeCategory, setActiveCategory] = useState("all");

  const visibleGroups =
    activeCategory === "all"
      ? skillGroups
      : skillGroups.filter((g) => g.id === activeCategory);

  return (
    <section
      id="skills"
      className="py-20 px-6 sm:px-12 lg:px-20 bg-white dark:bg-black text-neutral-900 dark:text-white relative border-t border-neutral-200/60 dark:border-neutral-900"
    >
      <div className="max-w-5xl mx-auto">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-12"
        >
          <span className="inline-block py-1 px-3 rounded-full bg-neutral-100 dark:bg-neutral-900 text-neutral-800 dark:text-neutral-300 text-xs font-semibold tracking-wider uppercase mb-3 border border-neutral-200/80 dark:border-neutral-800">
            {t("skills.badge")}
          </span>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-extrabold tracking-tight mb-4 text-neutral-900 dark:text-white">
            {t("skills.title")}
          </h2>
          <p className="text-neutral-600 dark:text-neutral-400 max-w-xl mx-auto text-base sm:text-lg">
            {t("skills.desc")}
          </p>
        </motion.div>

        {/* Category Filter Tabs */}
        <div className="flex flex-wrap items-center justify-center gap-2 mb-14">
          {skillCategories.map((category) => {
            const Icon = category.icon;
            const isActive = activeCategory === category.id;
            return (
              <button
                key={category.id}
                onClick={() => setActiveCategory(category.id)}
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl text-xs sm:text-sm font-semibold transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500/40
                  ${
                    isActive
                      ? "bg-neutral-950 text-white dark:bg-white dark:text-neutral-950 shadow-sm"
                      : "bg-neutral-100 text-neutral-600 dark:bg-neutral-900/60 dark:text-neutral-400 hover:bg-neutral-200 dark:hover:bg-neutral-800 hover:text-neutral-900 dark:hover:text-white"
                  }`}
              >
                <Icon size={15} />
                <span>{t(category.labelKey)}</span>
              </button>
            );
          })}
        </div>

        {/* Structured, Non-Clustered Skills Presentation */}
        <div className="space-y-12">
          <AnimatePresence mode="wait">
            <motion.div
              key={activeCategory}
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -15 }}
              transition={{ duration: 0.3 }}
              className="space-y-12"
            >
              {visibleGroups.map((group) => {
                const GroupIcon = group.icon;
                return (
                  <div key={group.id} className="relative">
                    {/* Domain Category Header */}
                    <div className="flex items-center gap-2.5 mb-5 pb-2.5 border-b border-neutral-200/60 dark:border-neutral-800/80">
                      <div className="w-7 h-7 rounded-lg bg-blue-500/10 text-blue-600 dark:text-blue-400 flex items-center justify-center">
                        <GroupIcon size={14} />
                      </div>
                      <h3 className="text-sm font-bold uppercase tracking-wider text-neutral-800 dark:text-neutral-200">
                        {t(group.titleKey)}
                      </h3>
                      <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-neutral-100 dark:bg-neutral-900 text-neutral-500 dark:text-neutral-400">
                        {group.skills.length}
                      </span>
                    </div>

                    {/* Clean Skill Cards Grid */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3.5">
                      {group.skills.map((skill) => {
                        const Icon = skill.icon;
                        return (
                          <div
                            key={skill.name}
                            className="group relative flex items-center gap-3.5 p-3.5 rounded-2xl
                                       bg-white dark:bg-neutral-950/70 
                                       border border-neutral-200/80 dark:border-neutral-800/80 
                                       hover:border-neutral-300 dark:hover:border-neutral-700 
                                       hover:shadow-sm transition-all duration-200"
                          >
                            {/* Brand Icon */}
                            <div className="w-10 h-10 shrink-0 rounded-xl flex items-center justify-center bg-neutral-100 dark:bg-neutral-900 border border-neutral-200/60 dark:border-neutral-800/80 group-hover:scale-105 transition-transform duration-200">
                              <Icon
                                className="text-xl"
                                style={{ color: skill.color }}
                              />
                            </div>

                            {/* Name & Tag */}
                            <div className="min-w-0 flex-1">
                              <h4 className="text-sm font-bold text-neutral-900 dark:text-white truncate group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                                {skill.name}
                              </h4>
                              <p className="text-xs text-neutral-500 dark:text-neutral-400 truncate mt-0.5">
                                {skill.tag}
                              </p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}

              {/* In 'all' view, display additional skills (e.g. Python) cleanly under its own group */}
              {activeCategory === "all" && additionalSkills.length > 0 && (
                <div className="relative pt-2">
                  <div className="flex items-center gap-2.5 mb-5 pb-2.5 border-b border-neutral-200/60 dark:border-neutral-800/80">
                    <div className="w-7 h-7 rounded-lg bg-blue-500/10 text-blue-600 dark:text-blue-400 flex items-center justify-center">
                      <Sparkles size={14} />
                    </div>
                    <h3 className="text-sm font-bold uppercase tracking-wider text-neutral-800 dark:text-neutral-200">
                      Scripting & Data Analysis
                    </h3>
                    <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-neutral-100 dark:bg-neutral-900 text-neutral-500 dark:text-neutral-400">
                      {additionalSkills.length}
                    </span>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3.5">
                    {additionalSkills.map((skill) => {
                      const Icon = skill.icon;
                      return (
                        <div
                          key={skill.name}
                          className="group relative flex items-center gap-3.5 p-3.5 rounded-2xl
                                     bg-white dark:bg-neutral-950/70 
                                     border border-neutral-200/80 dark:border-neutral-800/80 
                                     hover:border-neutral-300 dark:hover:border-neutral-700 
                                     hover:shadow-sm transition-all duration-200"
                        >
                          <div className="w-10 h-10 shrink-0 rounded-xl flex items-center justify-center bg-neutral-100 dark:bg-neutral-900 border border-neutral-200/60 dark:border-neutral-800/80 group-hover:scale-105 transition-transform duration-200">
                            <Icon
                              className="text-xl"
                              style={{ color: skill.color }}
                            />
                          </div>
                          <div className="min-w-0 flex-1">
                            <h4 className="text-sm font-bold text-neutral-900 dark:text-white truncate group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                              {skill.name}
                            </h4>
                            <p className="text-xs text-neutral-500 dark:text-neutral-400 truncate mt-0.5">
                              {skill.tag}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
}
